import React, { useState } from 'react';
import { 
  MapPin, Phone, Mail, Globe, Star, Gift, Copy, Check, 
  Share2, ExternalLink, BadgeCheck
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * BusinessCard - A shareable digital business card that Kata can present
 * Features:
 * - Contact info display
 * - Special offers with promo codes
 * - Copy to clipboard
 * - Share functionality
 * - Verified badge
 */
export default function BusinessCard({ business, compact = false }) {
  const [copied, setCopied] = useState(false);
  
  if (!business) return null;
  
  const handleCopyContact = () => {
    const contactInfo = `
${business.name}
${business.address || ''}
${business.phone ? `Phone: ${business.phone}` : ''}
${business.email ? `Email: ${business.email}` : ''}
${business.website ? `Website: ${business.website}` : ''}
${business.offer_code ? `Promo Code: ${business.offer_code}` : ''}
    `.trim();
    
    navigator.clipboard.writeText(contactInfo);
    setCopied(true);
    toast.success('Contact info copied!');
    setTimeout(() => setCopied(false), 2000);
  };
  
  const handleShare = async () => {
    const shareData = {
      title: business.name,
      text: `Check out ${business.name}${business.offer_title ? ` - ${business.offer_title}` : ''}`,
      url: business.website || window.location.href
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        handleCopyContact();
      }
    } else {
      handleCopyContact();
    }
  };
  
  // Compact card for chat messages
  if (compact) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-start gap-3">
          {business.logo_url && (
            <img 
              src={business.logo_url} 
              alt={business.name}
              className="w-12 h-12 rounded-lg object-cover"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-slate-900 truncate">{business.name}</h4>
              {business.verified && (
                <BadgeCheck className="w-4 h-4 text-sky-500 flex-shrink-0" />
              )}
            </div>
            <p className="text-xs text-slate-500">{business.category} • {business.location}</p>
            {business.rating > 0 && (
              <div className="flex items-center gap-1 mt-1">
                <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                <span className="text-xs text-slate-600">{business.rating}</span>
              </div>
            )}
          </div>
          {business.has_offer && (
            <div className="flex-shrink-0 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs px-2 py-1 rounded-full">
              <Gift className="w-3 h-3 inline mr-1" />
              Offer
            </div>
          )}
        </div>
        
        {business.has_offer && business.offer_title && (
          <div className="mt-2 bg-gradient-to-r from-orange-50 to-pink-50 border border-orange-200 rounded-lg p-2">
            <p className="text-xs font-medium text-orange-700">{business.offer_title}</p>
            {business.offer_code && (
              <code className="text-xs bg-white px-1.5 py-0.5 rounded border mt-1 inline-block">
                {business.offer_code}
              </code>
            )}
          </div>
        )}
        
        <div className="flex gap-2 mt-2">
          {business.phone && (
            <a href={`tel:${business.phone}`} className="text-sky-600 hover:text-sky-700">
              <Phone className="w-4 h-4" />
            </a>
          )}
          {business.website && (
            <a href={business.website} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:text-sky-700">
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
          <button onClick={handleCopyContact} className="text-slate-500 hover:text-slate-700 ml-auto">
            {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
      </div>
    );
  }
  
  // Full business card
  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-100 max-w-sm" data-testid="business-card">
      {/* Header with gradient */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-5 py-4 text-white">
        <div className="flex items-start gap-4">
          {business.logo_url ? (
            <img 
              src={business.logo_url} 
              alt={business.name}
              className="w-16 h-16 rounded-xl object-cover border-2 border-white/20"
            />
          ) : (
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center text-2xl font-bold">
              {business.name?.charAt(0)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-lg truncate">{business.name}</h3>
              {business.verified && (
                <BadgeCheck className="w-5 h-5 text-sky-400 flex-shrink-0" />
              )}
            </div>
            {business.tagline && (
              <p className="text-slate-300 text-sm truncate">{business.tagline}</p>
            )}
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                {business.category}
              </span>
              {business.rating > 0 && (
                <div className="flex items-center gap-1">
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                  <span className="text-xs">{business.rating}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Special Offer Banner */}
      {business.has_offer && business.offer_title && (
        <div className="bg-gradient-to-r from-orange-500 to-pink-500 px-5 py-3 text-white">
          <div className="flex items-center gap-2">
            <Gift className="w-5 h-5" />
            <div className="flex-1">
              <p className="font-semibold text-sm">{business.offer_title}</p>
              {business.offer_description && (
                <p className="text-xs text-white/90">{business.offer_description}</p>
              )}
            </div>
          </div>
          {business.offer_code && (
            <div className="mt-2 flex items-center gap-2">
              <code className="bg-white/20 px-3 py-1 rounded-lg text-sm font-mono">
                {business.offer_code}
              </code>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(business.offer_code);
                  toast.success('Promo code copied!');
                }}
                className="text-white/80 hover:text-white"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          )}
          {business.offer_expiry && (
            <p className="text-xs text-white/70 mt-1">Valid until {business.offer_expiry}</p>
          )}
        </div>
      )}
      
      {/* Contact Info */}
      <div className="p-5 space-y-3">
        {business.address && (
          <div className="flex items-start gap-3 text-sm">
            <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
            <span className="text-slate-700">{business.address}</span>
          </div>
        )}
        
        {business.phone && (
          <a href={`tel:${business.phone}`} className="flex items-center gap-3 text-sm text-slate-700 hover:text-sky-600 transition-colors">
            <Phone className="w-4 h-4 text-slate-400" />
            <span>{business.phone}</span>
          </a>
        )}
        
        {business.email && (
          <a href={`mailto:${business.email}`} className="flex items-center gap-3 text-sm text-slate-700 hover:text-sky-600 transition-colors">
            <Mail className="w-4 h-4 text-slate-400" />
            <span className="truncate">{business.email}</span>
          </a>
        )}
        
        {business.website && (
          <a href={business.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-sky-600 hover:text-sky-700 transition-colors">
            <Globe className="w-4 h-4" />
            <span className="truncate">Visit Website</span>
            <ExternalLink className="w-3 h-3 ml-auto" />
          </a>
        )}
      </div>
      
      {/* Actions */}
      <div className="px-5 pb-5 flex gap-2">
        <Button 
          variant="outline" 
          size="sm" 
          className="flex-1"
          onClick={handleCopyContact}
        >
          {copied ? <Check className="w-4 h-4 mr-1 text-green-500" /> : <Copy className="w-4 h-4 mr-1" />}
          {copied ? 'Copied!' : 'Copy'}
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          className="flex-1"
          onClick={handleShare}
        >
          <Share2 className="w-4 h-4 mr-1" />
          Share
        </Button>
      </div>
      
      {/* Footer branding */}
      <div className="bg-slate-50 px-5 py-2 text-center">
        <p className="text-xs text-slate-400">
          Shared via <span className="font-medium text-slate-600">Australian Hotspots</span>
        </p>
      </div>
    </div>
  );
}
